import {
  Button,
  Container,
  Form,
  Frequency,
  Input,
  Message,
  RadioGroup,
  Select,
  Stack,
  Text,
  TimeSpan,
} from "basis";
import { CreditCard } from "components/CreditCard/CreditCard";
import { FormActions } from "components/FormActions/FormActions";
import { Cancel } from "components/IncreaseCreditLimit/Cancel";
import { CancelLink } from "components/IncreaseCreditLimit/components/CancelLink";
import { Layout } from "components/Layout/Layout";
import { Separator } from "components/Separator/Separator";
import { formatAmount } from "library/moneyUtils";
import { yesNoOptions } from "library/sharedOptions";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { employmentAndIncomeTitle } from "../constants/constants";
import { nzEmploymentStatusOptions } from "../constants/nzEmploymentStatusOptions";
import { nzIndustryOptions } from "../constants/nzIndustryOptions";
import { nzOccupationOptions } from "../constants/nzOccupationOptions";
import { useCliContext } from "../hooks/useCliContext";
import { ActionTypes } from "../types";
import { getTotalMonthlyIncome } from "../utils";
import {
  nzValidateRequiredField,
  nzValidateTimeSpanField,
  validateFrequencyFieldFormV1,
} from "../validation/nzCustomValidation";

export const Income = () => {
  const {
    state: {
      accountInfo,
      employmentAndIncome: {
        employmentDetails,
        employerName,
        netIncome,
        otherEmploymentDetails,
        otherEmployerName,
        otherIncome,
        otherSourcesOfIncome,
        incomeChangesInFuture,
      },
    },
    dispatch,
  } = useCliContext();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const initialValues = {
    employmentDetails,
    employerName,
    netIncome,
    otherEmploymentDetails,
    otherEmployerName,
    otherIncome,
    otherSourcesOfIncome,
    incomeChangesInFuture,
  };

  const totalMonthlyIncomeParams = {
    netIncome,
    otherIncome,
    otherSourcesOfIncome,
  };

  const onSubmit = ({ values, errors }) => {
    if (Object.keys(errors).length === 0) {
      dispatch({
        type: ActionTypes.SUBMIT_EMPLOYMENT_INCOME,
        employmentAndIncome: {
          ...values,
          nzTotalIncome: getTotalMonthlyIncome(
            totalMonthlyIncomeParams,
            errors,
          ),
        },
      });
      navigate("/increase-credit-limit/expenses");
    }
  };

  return (
    <Layout>
      <Layout.Header>Request a credit limit increase</Layout.Header>
      <Layout.Main>
        <Form initialValues={initialValues} onSubmit={onSubmit}>
          {({ state: { values, errors } }) => {
            const totalMonthlyIncome = getTotalMonthlyIncome(values, errors);
            const showTotalMonthlyIncomeValue =
              totalMonthlyIncome === null || isNaN(totalMonthlyIncome)
                ? "–"
                : formatAmount(totalMonthlyIncome, {
                    withCents: true,
                  });
            return (
              <>
                <Stack gap="8">
                  <Container bg="grey.t05" padding="4">
                    <CreditCard
                      cardName={accountInfo?.cardName ?? ""}
                      cardEndsWith={accountInfo?.cardEndsWith}
                    />
                  </Container>
                  <Stack gap="4">
                    <Text>
                      <strong>Step 2 of 3</strong>
                    </Text>
                    <Text as="h3" textStyle="heading4">
                      {employmentAndIncomeTitle}
                    </Text>
                  </Stack>
                  <Separator />
                  <Stack gap="8" width="100%" width-sm="400">
                    <Text
                      as="h3"
                      textStyle="heading5"
                      color="primary.blue.t100"
                    >
                      {employmentAndIncomeTitle}
                    </Text>
                    <Select
                      name="employmentDetails.employmentStatus"
                      label="Employment status"
                      options={nzEmploymentStatusOptions}
                      validate={nzValidateRequiredField}
                      testId="employment-status"
                    />
                    <Select
                      name="employmentDetails.occupation"
                      label="Occupation"
                      options={nzOccupationOptions}
                      validate={nzValidateRequiredField}
                      testId="occupation"
                    />
                    <Select
                      name="employmentDetails.industry"
                      label="Industry"
                      options={nzIndustryOptions}
                      validate={nzValidateRequiredField}
                      testId="industry"
                    />
                    <Input
                      name="employerName"
                      label="Employer Name"
                      maxLength="30"
                    />
                    <TimeSpan
                      name="employmentDetails.timeWithCurrentEmployer"
                      label="Length of time at current employer"
                      validate={nzValidateTimeSpanField}
                      testId="time-current"
                    />
                    <Frequency
                      name="netIncome"
                      selectPlaceholder=""
                      mode="select"
                      label="Net income (after tax)"
                      amountPrefix="$"
                      annually={false}
                      amountMaxLength="8"
                      validate={validateFrequencyFieldFormV1}
                      testId="income"
                    />
                    <RadioGroup
                      name="otherSourcesOfIncome"
                      label="Do you have any other sources of income?"
                      options={yesNoOptions}
                      validate={nzValidateRequiredField}
                    />
                    {values.otherSourcesOfIncome === "yes" && (
                      <Stack gap="8" width="100%" width-sm="400">
                        <Separator />
                        <Text
                          as="h3"
                          textStyle="heading5"
                          color="primary.blue.t100"
                        >
                          Secondary income
                        </Text>
                        <Select
                          name="otherEmploymentDetails.employmentStatus"
                          label="Employment status"
                          options={nzEmploymentStatusOptions}
                          validate={nzValidateRequiredField}
                        />
                        {/* RL-709 temporarily hide secondary employment details section */}
                        {false && (
                          <Container>
                            <Select
                              name="otherEmploymentDetails.occupation"
                              label="Occupation"
                              options={nzOccupationOptions}
                              validate={nzValidateRequiredField}
                            />

                            <Select
                              name="otherEmploymentDetails.industry"
                              label="Industry"
                              options={nzIndustryOptions}
                              validate={nzValidateRequiredField}
                            />
                            <Input
                              name="otherEmployerName"
                              label="Employer Name"
                              maxLength="30"
                            />
                            <TimeSpan
                              name="otherEmploymentDetails.timeWithCurrentEmployer"
                              label="Length of time at current employer"
                              validate={nzValidateTimeSpanField}
                            />
                          </Container>
                        )}
                        <Frequency
                          name="otherIncome"
                          selectPlaceholder=""
                          mode="select"
                          label="Net income (after tax)"
                          amountPrefix="$"
                          annually={false}
                          amountMaxLength="8"
                          validate={validateFrequencyFieldFormV1}
                        />
                      </Stack>
                    )}
                    <Text>
                      <strong>
                        Your total monthly income (after tax):{" "}
                        {showTotalMonthlyIncomeValue}
                      </strong>
                    </Text>
                    <Separator />
                    <RadioGroup
                      name="incomeChangesInFuture"
                      label="Are you aware of any changes in the near future that may reduce your income?"
                      options={yesNoOptions}
                      validate={nzValidateRequiredField}
                    />
                    {values.incomeChangesInFuture === "yes" && (
                      <Message
                        severity="warning-or-significant"
                        bg="secondary.lightBlue.t25"
                      >
                        <strong>Please adjust your income</strong>
                        <br />
                        Please adjust your income to the expected lower amount
                        in the income fields above.
                      </Message>
                    )}
                    <FormActions>
                      <FormActions.Primary>
                        <Button variant="primary" type="submit">
                          Next
                        </Button>
                      </FormActions.Primary>
                      <FormActions.Secondary>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            dispatch({
                              type: ActionTypes.SUBMIT_EMPLOYMENT_INCOME,
                              employmentAndIncome: {
                                ...values,
                                nzTotalIncome: getTotalMonthlyIncome(
                                  values,
                                  errors,
                                ),
                              },
                            });
                            navigate(-1);
                          }}
                        >
                          Back
                        </Button>
                      </FormActions.Secondary>
                    </FormActions>
                    <CancelLink onClick={() => setIsModalOpen(true)} />
                  </Stack>
                  <Cancel
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onConfirm={() => {
                      navigate("/manage-account");
                    }}
                  />
                </Stack>
              </>
            );
          }}
        </Form>
      </Layout.Main>
    </Layout>
  );
};
